// src/constants/streamGames.js
// ============================================================================
// STREAMABLE GAME CATALOG (founder 2026-07-10) — streamers can categorize
// under ANY game with gameImages/{ABBR}.webp art: the full GAME_PROFILES
// catalog PLUS these browse-only games that have art in Storage but no
// gameplay/event profile yet. Keys are streamProfiles.game values (must pass
// the backend GAME_KEY_RE: [a-z0-9_]{1,32}).
//
// Storage inventory (2026-07-10): 30 webps — 20 covered by GAME_PROFILES
// abbrs (critcash_arena has no art), these 10 extras cover the rest.
// ============================================================================

import { GAME_PROFILES } from './gameData';

// genre/maxPlayers feed the open-catalog event profiles (communityGames.js):
// genre places each game under its real category pill; maxPlayers (racing
// only) is the showdown grid size and MUST match a payout structure —
// racing-mkw-top-6 = 12 grid, racing-f25-top-10 = 20 grid. allowedPlatforms
// (founder 2026-08-13) narrows the generic list to where the game actually
// runs; 'Cross-Platform' only where the game has full crossplay across its
// listed platforms; missing = the full COMMUNITY_ALLOWED_PLATFORMS list.
// Mirror of functions/constants/communityGames.js EXTRA_EVENT_GAMES — keep
// in sync.
export const EXTRA_STREAM_GAMES = {
  mkw: { displayName: 'Mario Kart World', abbr: 'MKW', genre: 'racing', maxPlayers: 12, allowedPlatforms: ['Nintendo Switch'] },
  f25: { displayName: 'F1 25', abbr: 'F25', genre: 'racing', maxPlayers: 20, allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S'] },
  efc: { displayName: 'EA Sports FC', abbr: 'EFC', genre: 'sports', allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Nintendo Switch'] },
  mts: { displayName: 'MLB The Show', abbr: 'MTS', genre: 'sports', allowedPlatforms: ['PS5', 'Xbox Series X|S', 'Nintendo Switch'] },
  sc2: { displayName: 'StarCraft 2', abbr: 'SC2', genre: 'rts', allowedPlatforms: ['PC'] },
  tw3: { displayName: 'Total War: Warhammer 3', abbr: 'TW3', genre: 'rts', allowedPlatforms: ['PC'] },
  wc3: { displayName: 'Warcraft 3', abbr: 'WC3', genre: 'rts', allowedPlatforms: ['PC'] },
  wow: { displayName: 'World of Warcraft', abbr: 'WOW', genre: 'other', allowedPlatforms: ['PC'] },
  ae2: { displayName: 'Age of Empires 2', abbr: 'AE2', genre: 'rts', allowedPlatforms: ['PC'] },
  sm2: { displayName: 'SMITE 2', abbr: 'SM2', genre: 'moba', allowedPlatforms: ['PC', 'PS5', 'Xbox Series X|S', 'Cross-Platform'] },
};

// Profile lookup across both catalogs.
export const streamGameInfo = (key) =>
  (key && (GAME_PROFILES?.[key] || EXTRA_STREAM_GAMES[key])) || null;

export const streamGameName = (key) =>
  streamGameInfo(key)?.displayName || (key ? key.toUpperCase() : '');

// Art lives at gameImages/{ABBR}.webp (abbr used VERBATIM — iRacing is the
// mixed-case 'iRC').
export const streamGameArtUrl = (key) => {
  const abbr = streamGameInfo(key)?.abbr;
  if (!abbr) return null;
  const encodedPath = encodeURIComponent(`gameImages/${abbr}.webp`);
  return `https://firebasestorage.googleapis.com/v0/b/critcash-45968.firebasestorage.app/o/${encodedPath}?alt=media`;
};

// Full pickable catalog: CritCash's 5 event games first, then everything
// else (profiles + extras) alphabetically. critcash_arena is internal.
const FEATURED_KEYS = ['mdn', 'cod', 'gt7', 'sf6', 'mvr'];

export const buildStreamGameCatalog = () => {
  const seen = new Set(FEATURED_KEYS);
  const rest = [];
  for (const [key, p] of Object.entries(GAME_PROFILES || {})) {
    if (!p?.abbr || key === 'critcash_arena' || seen.has(key)) continue;
    seen.add(key);
    rest.push({ key, name: p.displayName || key.toUpperCase() });
  }
  for (const [key, p] of Object.entries(EXTRA_STREAM_GAMES)) {
    if (seen.has(key)) continue;
    seen.add(key);
    rest.push({ key, name: p.displayName });
  }
  rest.sort((a, b) => a.name.localeCompare(b.name));
  const featured = FEATURED_KEYS
    .filter((key) => GAME_PROFILES?.[key]?.abbr)
    .map((key) => ({ key, name: GAME_PROFILES[key].displayName || key.toUpperCase() }));
  return [...featured, ...rest];
};
